import { MoreVertical } from "lucide-react";
import * as React from "react";

import type { ViewRecord } from "@/shared/domain/views";
import { Popover } from "@/shared/foundation/primitives";

import type { ManageViews } from "./useManageViews";
import styles from "./ViewRowMenu.module.css";

export interface ViewRowMenuProps {
  view: ViewRecord;
  manage: ManageViews;
  /** Absolute URL that reopens this View. */
  linkFor: (view: ViewRecord) => string;
  /** Start renaming — the row swaps its name for an input. */
  onRenameStart: (view: ViewRecord) => void;
  /** The copy exists; the picker decides what to do with it. */
  onDuplicated: (copy: ViewRecord) => void;
  /** Ask to delete; the picker owns the confirmation dialog. */
  onDeleteRequest: (view: ViewRecord) => void;
  /** Whether this is the View the caller starts this table from. */
  isDefault: boolean;
  /** Make this the caller's default, or take the default off it. */
  onToggleDefault: (view: ViewRecord) => void;
}

/**
 * Per-row actions on a saved View.
 *
 * The same five items on every row, personal and team, so there is one menu to
 * learn rather than one per tab.
 *
 * Rename and Delete are shown only to the author. Both rights derive from
 * authorship (ENG-2881), and an enabled control that always 401s is worse than
 * an absent one. This does cost org admins the shortcut on rows they did not
 * author — they may still edit through the API. Resolving real `can_edit` per
 * row would mean a second batched authz check on every list request, which is
 * a lot to spend on that one case.
 *
 * The two copy actions are offered to everyone: copying reads the source and
 * writes something new, so it needs only `can_view`. Their labels say what
 * they produce and do not change per tab, or the pair stops reading as a pair.
 *
 * Note there is no "Share with team" and no "Make private". A View's tier is
 * fixed at creation; reaching the other tier means copying into it.
 */
export function ViewRowMenu({
  view,
  manage,
  linkFor,
  onRenameStart,
  onDuplicated,
  onDeleteRequest,
  isDefault,
  onToggleDefault,
}: ViewRowMenuProps) {
  const [open, setOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const isAuthor = manage.isAuthor(view);

  const close = () => setOpen(false);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setCopied(false);
        }
      }}
      align="end"
      aria-label={`Actions for ${view.name}`}
      className={styles.menu}
      trigger={
        <button
          type="button"
          className={styles.trigger}
          aria-label={`Actions for ${view.name}`}
        >
          <MoreVertical size={15} aria-hidden />
        </button>
      }
    >
      <button
        type="button"
        className={styles.item}
        onClick={() => {
          // Deliberately does not close: the label is the only confirmation
          // that anything happened, and a menu that vanishes takes it away.
          void navigator.clipboard.writeText(linkFor(view)).then(() => {
            setCopied(true);
          });
        }}
      >
        {copied ? "Link copied" : "Copy link"}
      </button>
      {/* One item that reads its own state, not two of which one is always
          wrong. Offered to everyone: defaulting to a View changes nothing
          about the View, it records where this customer starts — so gating it
          on authorship would stop anyone starting from a team View. */}
      <button
        type="button"
        className={styles.item}
        onClick={() => {
          close();
          onToggleDefault(view);
        }}
      >
        {isDefault ? "Unset as default" : "Set as default"}
      </button>
      {isAuthor && (
        <button
          type="button"
          className={styles.item}
          onClick={() => {
            close();
            onRenameStart(view);
          }}
        >
          Rename
        </button>
      )}
      <button
        type="button"
        className={styles.item}
        disabled={manage.isBusy}
        onClick={() => {
          close();
          void manage.duplicateView(view).then(onDuplicated);
        }}
      >
        Create personal copy
      </button>
      <button
        type="button"
        className={styles.item}
        disabled={manage.isBusy}
        onClick={() => {
          close();
          void manage.createTeamCopy(view);
        }}
      >
        Create team copy
      </button>
      {isAuthor && (
        <>
          <div className={styles.separator} />
          <button
            type="button"
            className={styles.destructive}
            onClick={() => {
              close();
              onDeleteRequest(view);
            }}
          >
            Delete
          </button>
        </>
      )}
    </Popover>
  );
}
